// Explore.js — Explorer (Pinterest masonry) aligned with Admin layout
// ✅ Requirements implemented:
// 1) Admin-like layout + Pinterest masonry (natural/random heights)
// 2) User identity on each card: real avatar + username + surname (clickable to /users/:username)
// 3) Performance: progressive eligibility loading (no “load everything at once”), infinite scroll, lazy covers, skeletons, memoization
// 4) Notifications: local “Access granted” detection for invited users + clickable project link + “Since …” timestamp
// 5) Dates: use the same “Since …” formatter as Admin everywhere (cards + notifications)
//
// ⚠️ Keeps existing routes/features:
// - /projects, /projects/:id/roles, /projects/:id/media, /projects/:id/request-access, /projects/:id/my-access-request
// - Navigation: /project/:id/manage and /project/:id/manage?step=contribute
// - User profile route: /users/:username

import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const WV_LANG_KEY = "weavevo_lang";

const CACHE_TTL_MS = 2 * 60 * 1000;
const PAGE_SIZE = 24;                 // rendered items per “page”
const CANDIDATE_CHUNK = 12;           // projects evaluated per eligibility pass
const MAX_CONCURRENCY = 6;            // concurrent fetches for roles/media/status

const makeSeedImage = (title, id, w = 900, h = 700) =>
  `https://picsum.photos/seed/${encodeURIComponent((title || "Project") + "-" + id)}/${w}/${h}`;

// Module-scope caches (fast back/forward navigation)
const EXPLORE_CACHE = new Map();     // key(username) -> { candidates, eligible, rolesMap, requestSent, coverSrcById, ts, scanIndex }
const COVER_AR_CACHE = new Map();    // src -> aspectRatio (w/h)

// i18n — same lightweight approach as LandingPage.js
const EXP_I18N = {
  en: {
    // topbar
    chip_all: "All",
    chip_public: "Open for Weaving",
    chip_private: "Request to join",
    search_placeholder: "Search (title, description, owner)…",

    // states
    loading_projects: "Loading projects…",
    loading_more: "Loading more…",
    empty: "No project matches your filters.",

    // card
    untitled: "Untitled",
    anonymous: "Anonymous",
    status: "Status",
    participants: "Participants",
    created: "Created",
    public_pill: "Open for Weaving",
    private_pill: "Request to join",
    request_sent: "Request sent",
    pending_validation: "Pending approval",

    // actions
    view: "View",
    weave: "Weave",
    request_access: "Request access",

    // request flow
    login_to_request: "Please log in to request access.",
    request_message: "I'd like to join the project {title}.",
    request_error: "Could not send the request.",
    request_network_error: "Network error while sending the request.",

    // notifications
    notifications: "Notifications",
    access_granted: "Access granted",
    open_project: "Open project",
    no_notifications: "No notifications yet.",
  },
  fr: {
    // topbar
    chip_all: "Tous",
    chip_public: "Open for Weaving",
    chip_private: "Request to join",
    search_placeholder: "Rechercher (titre, description, propriétaire)…",

    // states
    loading_projects: "Chargement des projets…",
    loading_more: "Chargement…",
    empty: "Aucun projet ne correspond à vos filtres.",

    // card
    untitled: "Sans titre",
    anonymous: "Anonyme",
    status: "Statut",
    participants: "Participants",
    created: "Créé",
    public_pill: "Open for Weaving",
    private_pill: "Request to join",
    request_sent: "Demande envoyée",
    pending_validation: "En attente de validation",

    // actions
    view: "Voir",
    weave: "Weaver",
    request_access: "Demander l’accès",

    // request flow
    login_to_request: "Veuillez vous connecter pour demander un accès.",
    request_message: "Je souhaite rejoindre le projet {title}.",
    request_error: "Erreur lors de l'envoi de la demande.",
    request_network_error: "Erreur réseau lors de l'envoi de la demande.",

    // notifications
    notifications: "Notifications",
    access_granted: "Accès accordé",
    open_project: "Ouvrir le projet",
    no_notifications: "Aucune notification pour le moment.",
  },
};

function clamp(n, a, b) {
  const x = Number(n);
  if (!Number.isFinite(x)) return a;
  return Math.max(a, Math.min(b, x));
}

function safeLower(s) {
  return String(s || "").trim().toLowerCase();
}

function getInitials(name = "") {
  const s = String(name || "").trim();
  if (!s) return "AN";
  const parts = s.split(/[\s._-]+/g).filter(Boolean);
  const a = (parts[0] || "A")[0] || "A";
  const b = (parts[1] || parts[0] || "N")[0] || "N";
  return (a + b).toUpperCase();
}

async function safeJson(res, fallback) {
  try {
    if (!res || !res.ok) return fallback;
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return fallback;
    return await res.json();
  } catch {
    return fallback;
  }
}

async function runPool(items, worker, limit = 6) {
  const arr = Array.isArray(items) ? items : [];
  const lim = Math.max(1, Math.min(16, Number(limit) || 6));
  const out = new Array(arr.length);
  let idx = 0;

  const runners = new Array(Math.min(lim, arr.length)).fill(0).map(async () => {
    while (idx < arr.length) {
      const i = idx++;
      try {
        out[i] = await worker(arr[i], i);
      } catch (e) {
        out[i] = undefined;
        void e;
      }
    }
  });

  await Promise.all(runners);
  return out;
}

function useOnceNearScreen(rootMargin = "1200px 0px") {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el) return;

    if (typeof window === "undefined" || typeof window.IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }

    const obs = new window.IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && e.isIntersecting) {
          setSeen(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [seen, rootMargin]);

  return [ref, seen];
}

function LazyCover({ src, title }) {
  const holderRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Default ratio close to Pinterest thumbnails, then adjust after load.
  const [aspectRatio, setAspectRatio] = useState(() => {
    const cached = COVER_AR_CACHE.get(src);
    return typeof cached === "number" && Number.isFinite(cached) && cached > 0 ? cached : 4 / 3;
  });

  const setARFromWH = useCallback(
    (w, h) => {
      const W = Number(w);
      const H = Number(h);
      if (!Number.isFinite(W) || !Number.isFinite(H) || W <= 0 || H <= 0) return;
      const r = W / H;
      if (!Number.isFinite(r) || r <= 0.05 || r >= 50) return;

      // ✅ allow variation (masonry “random”), but prevent extreme tall covers
      const clamped = clamp(r, 0.65, 2.55);
      COVER_AR_CACHE.set(src, clamped);
      setAspectRatio(clamped);
    },
    [src]
  );

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;

    if (typeof window === "undefined" || typeof window.IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const obs = new window.IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && e.isIntersecting) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "900px 0px" }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={holderRef} className="wv-thumb-wrap" style={{ aspectRatio }} aria-label={title || "cover"}>
      {!shouldLoad ? (
        <div className="wv-skeleton" />
      ) : (
        <img
          src={src}
          alt={title || "project cover"}
          className="wv-thumb"
          loading="lazy"
          decoding="async"
          onLoad={(e) => {
            const img = e.currentTarget;
            setARFromWH(img?.naturalWidth, img?.naturalHeight);
          }}
        />
      )}
    </div>
  );
}

const SkeletonPin = React.memo(function SkeletonPin({ i = 0 }) {
  const ratios = [0.8, 0.95, 1.1, 1.25, 1.45, 1.65, 1.9, 2.2];
  const ar = ratios[i % ratios.length] || 1.2;

  return (
    <div className="wv-masonry-item" aria-hidden="true" style={{ paddingTop: '15px' }}>
      <div className="wv-pin">
        <div className="wv-thumb-wrap" style={{ aspectRatio: ar }}>
          <div className="wv-skeleton" style={{ minHeight: 160 }} />
        </div>
        <div className="wv-body">
          <div className="wv-skel-line wv-skel-line--title" />
          <div className="wv-skel-line" />
          <div className="wv-skel-line" style={{ width: "70%" }} />
          <div className="wv-skel-row">
            <div className="wv-skel-avatar" />
            <div className="wv-skel-line" style={{ width: "55%" }} />
          </div>
        </div>
      </div>
    </div>
  );
});

const UserIdentity = React.memo(function UserIdentity({
  username,
  surname,
  avatarSrc,
  profilePath,
  onNavigate,
}) {
  const displaySurname = String(surname || "").trim();
  const displayUsername = String(username || "").trim();

  const content = (
    <span className="wv-ownerInner">
      <span className="wv-avatar">
        {avatarSrc ? (
          <img src={avatarSrc} alt={displayUsername} className="wv-avatarImg" loading="lazy" decoding="async" />
        ) : (
          <span className="wv-avatarFallback">{getInitials(displayUsername || displaySurname)}</span>
        )}
      </span>

      <span className="wv-ownerText">
        <span className="wv-ownerUser">{displayUsername || "—"}</span>
        {displaySurname ? <span className="wv-ownerSurname">{displaySurname}</span> : null}
      </span>
    </span>
  );

  // Keep layout identical even if no profilePath
  if (profilePath) {
    return (
      <Link className="wv-ownerLink" to={profilePath} onClick={onNavigate} title={displayUsername}>
        {content}
      </Link>
    );
  }

  return (
    <span className="wv-ownerLink wv-ownerLink--disabled" title={displayUsername}>
      {content}
    </span>
  );
});

const ProjectCard = React.memo(function ProjectCard({
  project,
  coverSrc,
  ownerUsername,
  ownerSurname,
  ownerAvatarSrc,
  ownerProfilePath,
  isPublic,
  participantsCount,
  createdAt,
  requestSent,
  t,
  onCardSeen,
  onOpenRead,
  onOpenWeave,
  onRequestAccess,
  onPrimaryClick,
}) {
  const title = String(project?.title || t("untitled")).trim();
  const desc = project?.description || "—";
  const [ref, seen] = useOnceNearScreen("1400px 0px");

  useEffect(() => {
    if (seen) onCardSeen(project);
  }, [seen, onCardSeen, project]);

  return (
    <div ref={ref} className="wv-masonry-item" style={{ paddingTop: '15px' }}>
      <div className="wv-pin">
        {/* Cover */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => onPrimaryClick(project)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onPrimaryClick(project);
            }
          }}
          title={isPublic ? t("view") : t("request_access")}
          style={{ cursor: "pointer" }}
        >
          <LazyCover src={coverSrc} title={title} />
        </div>

        {/* Body */}
        <div className="wv-body">
          <div className="wv-titleRow">
            <h6 className="wv-title" title={title}>
              {title}
            </h6>

            <div className="wv-pills">
              <span className="wv-pill" title={t("created")}>
                <i className="bi bi-clock-history" /> {createdAt}
              </span>
              <span className="wv-pill" title={t("participants")}>
                <i className="bi bi-people" /> {Number.isFinite(participantsCount) ? participantsCount : "—"}
              </span>
            </div>
          </div>

          <p className="wv-desc">{desc}</p>

          <div className="wv-meta">
            <div className="wv-owner">
              <UserIdentity
                username={ownerUsername}
                surname={ownerSurname}
                avatarSrc={ownerAvatarSrc}
                profilePath={ownerProfilePath}
                onNavigate={(e) => {
                  // Prevent card click when clicking identity
                  e.stopPropagation();
                }}
              />
            </div>

            <div className="wv-pills">
              <span className="wv-pill" title={isPublic ? t("public_pill") : t("private_pill")}>
                <i className={`bi ${isPublic ? "bi-globe" : "bi-lock"}`} />
                {isPublic ? t("public_pill") : t("private_pill")}
              </span>

              {requestSent ? (
                <span className="wv-pill wv-pill-ok" title={t("request_sent")}>
                  <i className="bi bi-check2" /> {t("request_sent")}
                </span>
              ) : null}
            </div>
          </div>

          {/* Actions (Wizard style) */}
          <div className="wv-actions">
            {isPublic ? (
              <>
                <button className="btn btn-outline-primary btn-weave btn-sm" onClick={() => onOpenRead(project)} title={t("view")}>
                  <i className="bi bi-eye" /> {t("view")}
                </button>

                <button className="btn btn-primary btn-weave btn-sm" onClick={() => onOpenWeave(project)} title={t("weave")}>
                  <i className="bi bi-stars" /> {t("weave")}
                </button>
              </>
            ) : requestSent ? (
              <span className="wv-statusText" title={t("pending_validation")}>
                <i className="bi bi-shield-check" /> {t("pending_validation")}
              </span>
            ) : (
              <button className="btn btn-primary btn-weave btn-sm" onClick={() => onRequestAccess(project)} title={t("request_access")}>
                <i className="bi bi-envelope" /> {t("request_access")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default function Explore() {
  const { token } = useAuth();
  const navigate = useNavigate();

  // Language (same logic as LandingPage.js): default EN, optional FR via localStorage
  const [lang, setLang] = useState(() => {
    try {
      const raw = localStorage.getItem(WV_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
      const norm = String(raw || "").toLowerCase().slice(0, 2);
      return norm === "fr" ? "fr" : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem(WV_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
        const norm = String(raw || "").toLowerCase().slice(0, 2);
        const next = norm === "fr" ? "fr" : "en";
        setLang(next);
        if (!raw) localStorage.setItem(WV_LANG_KEY, next);
      } catch (e) {
        void e;
      }
    };

    read();
    window.addEventListener("storage", read);
    window.addEventListener("focus", read);
    return () => {
      window.removeEventListener("storage", read);
      window.removeEventListener("focus", read);
    };
  }, []);

  const locale = useMemo(() => (lang === "fr" ? "fr-FR" : "en-US"), [lang]);

  const t = useCallback(
    (key, vars) => {
      const dict = EXP_I18N?.[lang] || EXP_I18N?.en || {};
      let out = dict[key] ?? EXP_I18N?.en?.[key] ?? key;
      if (vars && typeof out === "string") {
        out = out.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
      }
      return out;
    },
    [lang]
  );

  // ✅ Admin-like “Since …” formatter (copied from Admin.js)
  const formatDate = useCallback(
    (raw) => {
      if (raw == null) return "";

      const ts =
        typeof raw === "number"
          ? raw
          : /^[0-9]{11,}$/.test(String(raw).trim())
          ? Number(String(raw).trim())
          : new Date(raw).getTime();

      if (!Number.isFinite(ts)) return "";

      const now = Date.now();
      const diffMs = now - ts;
      const isFuture = diffMs < 0;

      const absSec = Math.floor(Math.abs(diffMs) / 1000);
      if (absSec < 5) return lang === "fr" ? "à l’instant" : "just now";

      const units = [
        { sec: 31536000, en: ["year", "years"], fr: ["an", "ans"] },
        { sec: 2592000, en: ["month", "months"], fr: ["mois", "mois"] },
        { sec: 604800, en: ["week", "weeks"], fr: ["semaine", "semaines"] },
        { sec: 86400, en: ["day", "days"], fr: ["jour", "jours"] },
        { sec: 3600, en: ["hour", "hours"], fr: ["heure", "heures"] },
        { sec: 60, en: ["minute", "minutes"], fr: ["minute", "minutes"] },
        { sec: 1, en: ["second", "seconds"], fr: ["seconde", "secondes"] },
      ];

      const u = units.find((x) => absSec >= x.sec) || units[units.length - 1];
      const value = Math.max(1, Math.floor(absSec / u.sec));
      const n = value.toLocaleString(locale);
      const label = (lang === "fr" ? u.fr : u.en)[value > 1 ? 1 : 0];

      if (isFuture) return lang === "fr" ? `dans ${n} ${label}` : `in ${n} ${label}`;
      return lang === "fr" ? `depuis ${n} ${label}` : `since ${n} ${label}`;
    },
    [lang, locale]
  );

  // Inline notice (avoids alert() and keeps UX clean)
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    if (!notice) return undefined;
    const h = setTimeout(() => setNotice(null), 2800);
    return () => clearTimeout(h);
  }, [notice]);

  // User identity cache (avatars + surname)
  const [usersByUsername, setUsersByUsername] = useState({});
  const avatarBustRef = useRef(0);

  const avatarFor = useCallback(
    (username, labelForFallback = "") => {
      const u = usersByUsername?.[username];
      const maybe = u?.avatar_url || u?.avatarUrl || u?.avatar || u?.photo || u?.picture;
      if (maybe) return maybe;

      const profilePath = u?.profilepath || u?.profilePath || u?.profile_path;
      if (profilePath) {
        // keep compatible with stored values (can be "uploads/..." or "/uploads/...")
        return profilePath.startsWith("/") ? profilePath : `/${profilePath}`;
      }

      // clean placeholder
      const label = String(labelForFallback || username || "").trim() || "User";
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(label)}&background=EEF2FF&color=111827&bold=true&size=128`;
    },
    [usersByUsername]
  );

  const resolveSurname = useCallback((u) => {
    if (!u || typeof u !== "object") return "";
    return (
      u.surname ||
      u.last_name ||
      u.lastname ||
      u.family_name ||
      u.familyName ||
      u.nom ||
      u.lastName ||
      ""
    );
  }, []);

  const resolveUserProfilePath = useCallback((username) => {
    const u = String(username || "").trim();
    if (!u) return null;
    return `/users/${encodeURIComponent(u)}`;
  }, []);

  // Load users lazily (still a single request, cached by browser + state)
  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    (async () => {
      const res = await fetch("/users", { headers: { Authorization: `Bearer ${token}` } });
      const arr = await safeJson(res, []);
      if (cancelled) return;

      const map = {};
      (arr || []).forEach((u) => {
        if (u?.username) map[u.username] = u;
      });

      setUsersByUsername(map);
      avatarBustRef.current = Date.now();
    })().catch(() => {
      if (!cancelled) setUsersByUsername({});
    });

    return () => {
      cancelled = true;
    };
  }, [token]);

  // 🔎 Filters
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), 180);
    return () => clearTimeout(handle);
  }, [query]);

  // ✅ Topbar dynamic spacer to avoid overlap
  const topbarRef = useRef(null);
  const [topbarH, setTopbarH] = useState(76);
  useEffect(() => {
    const el = topbarRef.current;
    if (!el || typeof window === "undefined" || typeof window.ResizeObserver === "undefined") return;

    const update = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (!h) return;
      setTopbarH((prev) => (prev === h ? prev : h));
    };

    update();
    const ro = new window.ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Identity
  const [username, setUsername] = useState("");
  const usernameLowerRef = useRef("");
  const isFresh = (ts) => Date.now() - ts < CACHE_TTL_MS;

  // Data: candidates -> progressively eligible
  const [candidates, setCandidates] = useState([]);
  const candidatesRef = useRef([]);
  const [eligibleProjects, setEligibleProjects] = useState([]);
  const eligibleRef = useRef([]);
  const scanIndexRef = useRef(0);
  const [hasMoreCandidates, setHasMoreCandidates] = useState(false);

  const [rolesMap, setRolesMap] = useState({});
  const rolesRef = useRef({});

  // Cover + request status maps (lazy per visible card)
  const [coverSrcById, setCoverSrcById] = useState({});
  const coverRef = useRef({});
  const coverLoadingRef = useRef(new Set());

  const [requestSentProjects, setRequestSentProjects] = useState({});
  const requestRef = useRef({});
  const requestLoadingRef = useRef(new Set());

  // Paging
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  // Project lookup (for notifications)
  const projectById = useMemo(() => {
    const map = {};
    for (const p of candidates) map[p.id] = p;
    for (const p of eligibleProjects) map[p.id] = p;
    return map;
  }, [candidates, eligibleProjects]);

  // Keep refs in sync (avoids re-render loops)
  useEffect(() => {
    candidatesRef.current = candidates;
  }, [candidates]);
  useEffect(() => {
    eligibleRef.current = eligibleProjects;
  }, [eligibleProjects]);
  useEffect(() => {
    rolesRef.current = rolesMap;
  }, [rolesMap]);
  useEffect(() => {
    coverRef.current = coverSrcById;
  }, [coverSrcById]);
  useEffect(() => {
    requestRef.current = requestSentProjects;
  }, [requestSentProjects]);

  const isPublicProject = useCallback((project) => (project?.visibility || "PRIVATE") === "PUBLIC", []);

  const openRead = useCallback((project) => navigate(`/project/${project.id}/studio`), [navigate]);
  const openWeave = useCallback((project) => navigate(`/project/${project.id}/contributor-studio`), [navigate]);

  const fetchRolesForIds = useCallback(
    async (projectIds) => {
      const ids = Array.isArray(projectIds) ? projectIds.filter(Boolean) : [];
      const out = {};

      await runPool(
        ids,
        async (pid) => {
          const res = await fetch(`/projects/${pid}/roles`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          const j = await safeJson(res, []);
          out[pid] = Array.isArray(j) ? j : [];
          return null;
        },
        MAX_CONCURRENCY
      );

      return out;
    },
    [token]
  );

  const loadMoreEligible = useCallback(
    async (minAdd = PAGE_SIZE) => {
      if (!token) return;
      if (loadingMoreRef.current) return;
      if (!hasMoreCandidates) return;

      loadingMoreRef.current = true;
      setIsLoadingMore(true);

      try {
        let added = 0;
        while (added < minAdd && scanIndexRef.current < candidatesRef.current.length) {
          const chunk = candidatesRef.current.slice(scanIndexRef.current, scanIndexRef.current + CANDIDATE_CHUNK);
          scanIndexRef.current += chunk.length;

          if (chunk.length === 0) break;

          // roles determine membership eligibility
          const rolesById = await fetchRolesForIds(chunk.map((p) => p.id));

          setRolesMap((prev) => ({ ...prev, ...rolesById }));

          const myU = usernameLowerRef.current;

          const notMember = chunk.filter((p) => {
            const roles = rolesById[p.id] || [];
            return !roles.some((r) => safeLower(r?.username || r?.user || r?.login) === myU);
          });

          if (notMember.length) {
            setEligibleProjects((prev) => prev.concat(notMember));
            added += notMember.length;
          }
        }

        const hasMore = scanIndexRef.current < candidatesRef.current.length;
        setHasMoreCandidates(hasMore);
      } finally {
        loadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    },
    [token, hasMoreCandidates, fetchRolesForIds]
  );

  const ensureCoverForProject = useCallback(
    async (project) => {
      const pid = project?.id;
      if (!pid) return;

      // If identity_image exists, keep it (fastest)
      if (project?.identity_image) return;

      // Already upgraded once
      if (coverLoadingRef.current.has(pid)) return;

      // If cover already points to uploads (likely from media), do nothing
      const current = coverRef.current?.[pid] || "";
      if (current && String(current).includes("/uploads/") && !String(current).includes("picsum.photos")) return;

      coverLoadingRef.current.add(pid);

      try {
        const res = await fetch(`/projects/${pid}/media`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const list = await safeJson(res, []);

        // Pick a real image, avoid videos
        const img = (list || []).find((m) => String(m?.file_path || "").match(/\.(jpg|jpeg|png|webp|gif)$/i));
        if (img?.file_path) {
          const src = `/uploads/${img.file_path}`;
          setCoverSrcById((prev) => (prev[pid] === src ? prev : { ...prev, [pid]: src }));
        }
      } catch (e) {
        void e;
      } finally {
        coverLoadingRef.current.delete(pid);
      }
    },
    [token]
  );

  const ensureMyAccessRequestStatus = useCallback(
    async (project) => {
      const pid = project?.id;
      if (!pid || !token) return;
      if (requestRef.current?.[pid] != null) return;
      if (requestLoadingRef.current.has(pid)) return;

      // only meaningful for private projects
      if (isPublicProject(project)) return;

      requestLoadingRef.current.add(pid);
      try {
        const r = await fetch(`/projects/${pid}/my-access-request`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = await safeJson(r, null);
        const exists = !!(j && (j.exists === true || j.requested === true || j.pending === true));
        setRequestSentProjects((prev) => ({ ...prev, [pid]: exists }));
      } catch (e) {
        void e;
      } finally {
        requestLoadingRef.current.delete(pid);
      }
    },
    [token, isPublicProject]
  );

  const onCardSeen = useCallback(
    (project) => {
      // Lazy-enrich visible cards only
      void ensureCoverForProject(project);
      void ensureMyAccessRequestStatus(project);
    },
    [ensureCoverForProject, ensureMyAccessRequestStatus]
  );

  const onRequestAccess = useCallback(
    async (project) => {
      if (!token) {
        setNotice({ type: "warn", text: t("login_to_request") });
        return;
      }

      try {
        const res = await fetch(`/projects/${project.id}/request-access`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: t("request_message", { title: project.title || t("untitled") }),
          }),
        });

        if (res.ok) {
          setRequestSentProjects((prev) => ({ ...prev, [project.id]: true }));
        } else {
          setNotice({ type: "error", text: t("request_error") });
        }
      } catch (e) {
        void e;
        setNotice({ type: "error", text: t("request_network_error") });
      }
    },
    [token, t]
  );

  const handlePrimaryClick = useCallback(
    (project) => {
      const isPublic = isPublicProject(project);

      if (isPublic) {
        openRead(project);
        return;
      }

      // Private project: request to join
      if (requestRef.current?.[project.id]) return;
      onRequestAccess(project);
    },
    [isPublicProject, openRead, onRequestAccess]
  );

  // ✅ Fetch candidates (light) + progressively build eligible list
  useEffect(() => {
    if (!token) return;

    const decoded = jwtDecode(token);
    const myUsername = decoded?.preferred_username || "";
    setUsername(myUsername);
    usernameLowerRef.current = safeLower(myUsername);

    const cached = EXPLORE_CACHE.get(myUsername);
    if (cached && isFresh(cached.ts)) {
      setCandidates(cached.candidates || []);
      setEligibleProjects(cached.eligible || []);
      setRolesMap(cached.rolesMap || {});
      setRequestSentProjects(cached.requestSent || {});
      setCoverSrcById(cached.coverSrcById || {});
      scanIndexRef.current = Number.isFinite(cached.scanIndex) ? cached.scanIndex : 0;
      setHasMoreCandidates(scanIndexRef.current < (cached.candidates || []).length);
      setIsLoadingInitial(false);
      setDisplayCount(PAGE_SIZE);
      return;
    }

    setIsLoadingInitial(true);

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch("/projects", { headers: { Authorization: `Bearer ${token}` } });
        const all = await safeJson(res, []);
        if (cancelled) return;

        // “Candidates” = Active && not owner (membership evaluated lazily)
        const actives = (all || []).filter((p) => {
          const status = p?.status || "Draft";
          if (status !== "Active") return false;

          const isOwner = safeLower(p?.created_by) === safeLower(myUsername);
          return !isOwner;
        });

        // Prime cover map with identity_image/seed (no media fetch yet)
        const coverInit = {};
        actives.forEach((p) => {
          if (p?.identity_image) coverInit[p.id] = `/uploads/${p.identity_image}`;
          else coverInit[p.id] = makeSeedImage(p.title, p.id);
        });

        setCandidates(actives);
        setEligibleProjects([]);
        setRolesMap({});
        setRequestSentProjects({});
        setCoverSrcById(coverInit);

        scanIndexRef.current = 0;
        setHasMoreCandidates(actives.length > 0);

        // Cache light data
        EXPLORE_CACHE.set(myUsername, {
          candidates: actives,
          eligible: [],
          rolesMap: {},
          requestSent: {},
          coverSrcById: coverInit,
          ts: Date.now(),
          scanIndex: 0,
        });

        // Load first batch eligibility (non-blocking skeletons are shown meanwhile)
        setTimeout(() => {
          if (!cancelled) void loadMoreEligible(PAGE_SIZE);
        }, 0);
      } finally {
        if (!cancelled) setIsLoadingInitial(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, loadMoreEligible]);

  // Cache incremental state (throttled-ish by React batching)
  useEffect(() => {
    const u = String(username || "").trim();
    if (!u) return;
    const cached = EXPLORE_CACHE.get(u);
    if (!cached) return;

    EXPLORE_CACHE.set(u, {
      ...cached,
      eligible: eligibleProjects,
      rolesMap,
      requestSent: requestSentProjects,
      coverSrcById,
      ts: cached.ts, // keep original ts for TTL
      scanIndex: scanIndexRef.current,
    });
  }, [username, eligibleProjects, rolesMap, requestSentProjects, coverSrcById]);

  // Infinite scroll sentinel
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    if (typeof window === "undefined" || typeof window.IntersectionObserver === "undefined") return;

    const io = new window.IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && e.isIntersecting) {
          setDisplayCount((prev) => prev + PAGE_SIZE);
        }
      },
      { rootMargin: "1200px 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Ensure we have enough eligible items to satisfy current displayCount (and filters)
  const filteredEligible = useMemo(() => {
    const q = safeLower(debouncedQuery);
    const list = eligibleProjects;

    return (list || []).filter((p) => {
      if (!p) return false;

      const isPublic = isPublicProject(p);
      if (visibilityFilter === "public" && !isPublic) return false;
      if (visibilityFilter === "private" && isPublic) return false;

      if (!q) return true;

      const title = safeLower(p.title);
      const desc = safeLower(p.description);
      const owner = safeLower(p.created_by);

      return title.includes(q) || desc.includes(q) || owner.includes(q);
    });
  }, [eligibleProjects, debouncedQuery, visibilityFilter, isPublicProject]);

  useEffect(() => {
    if (!token) return;
    if (!hasMoreCandidates) return;
    if (isLoadingInitial) return;
    if (isLoadingMore) return;

    // If filters reduce visible results, keep loading more to try to find matches
    if (filteredEligible.length < displayCount) {
      void loadMoreEligible(PAGE_SIZE);
    }
  }, [token, hasMoreCandidates, isLoadingInitial, isLoadingMore, filteredEligible.length, displayCount, loadMoreEligible]);

  // Reset visible window on filter changes (feels snappier and avoids “empty then jump”)
  useEffect(() => {
    setDisplayCount(PAGE_SIZE);
  }, [visibilityFilter, debouncedQuery]);

  const visibleProjects = useMemo(() => filteredEligible.slice(0, displayCount), [filteredEligible, displayCount]);

  // Notifications (local “access granted” detector)
  const WV_NOTIF_SEEN_KEY = "weavevo_explorer_notif_seen_v1";
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  const notifSeenRef = useRef(new Set());

  useEffect(() => {
    try {
      const raw = localStorage.getItem(WV_NOTIF_SEEN_KEY) || "[]";
      const arr = JSON.parse(raw);
      const set = new Set((Array.isArray(arr) ? arr : []).map(String));
      notifSeenRef.current = set;
    } catch {
      notifSeenRef.current = new Set();
    }
  }, []);

  const pushNotif = useCallback((item) => {
    if (!item || !item.id) return;
    const id = String(item.id);
    if (notifSeenRef.current.has(id)) return;

    notifSeenRef.current.add(id);
    try {
      localStorage.setItem(WV_NOTIF_SEEN_KEY, JSON.stringify(Array.from(notifSeenRef.current).slice(-500)));
    } catch (e) {
      void e;
    }

    setNotifItems((prev) => [item, ...prev].slice(0, 50));
  }, []);

  const checkAccessGranted = useCallback(async () => {
    if (!token) return;

    // Only check projects where a request was sent
    const requestedIds = Object.entries(requestRef.current || {})
      .filter(([, v]) => !!v)
      .map(([k]) => Number(k) || k);

    if (!requestedIds.length) return;

    await runPool(
      requestedIds,
      async (pid) => {
        const myU = usernameLowerRef.current;
        if (!myU) return;

        // Avoid repeated checks if already notified
        const notifId = `access_granted:${myU}:${pid}`;
        if (notifSeenRef.current.has(notifId)) return;

        const res = await fetch(`/projects/${pid}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const roles = await safeJson(res, []);
        const arr = Array.isArray(roles) ? roles : [];
        const mine = arr.find((r) => safeLower(r?.username || r?.user || r?.login) === myU);

        if (mine) {
          const p = projectById?.[pid] || {};
          const title = String(p?.title || t("untitled")).trim();
          const createdAt = mine?.created_at || mine?.createdAt || Date.now();

          pushNotif({
            id: notifId,
            type: "access_granted",
            projectId: pid,
            title,
            created_at: createdAt,
            link: `/project/${pid}/studio`,
          });
        }
      },
      4
    );
  }, [token, projectById, pushNotif, t]);

  useEffect(() => {
    if (!token) return;
    // run immediately, then poll
    void checkAccessGranted();
    const it = setInterval(() => void checkAccessGranted(), 30000);
    return () => clearInterval(it);
  }, [token, checkAccessGranted, requestSentProjects]);

  const styles = `
    /* Base */
    .wv-notice{
      position: sticky;
      top: 0;
      z-index: 55;
      margin: 12px auto 0;
      width: min(980px, calc(100% - 24px));
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 10px;
      padding: 10px 12px;
      border-radius: 14px;
      border: 1px solid rgba(255,255,255,.14);
      background: rgba(20,20,22,.68);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      box-shadow: 0 10px 32px rgba(0,0,0,.22);
      color: rgba(255,255,255,.92);
    }
    .wv-notice--warn{ border-color: rgba(255,193,7,.28); }
    .wv-notice--error{ border-color: rgba(255,77,79,.28); }
    .wv-noticeClose{
      border:0;
      background: rgba(255,255,255,.10);
      color: rgba(255,255,255,.92);
      width: 28px;
      height: 28px;
      border-radius: 10px;
      cursor: pointer;
    }
    .wv-noticeClose:hover{ background: rgba(255,255,255,.16); }

    .explore-pin, .explore-pin *, .explore-pin *::before, .explore-pin *::after{ box-sizing: border-box; }
    .explore-pin{
      font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, "Apple Color Emoji", "Segoe UI Emoji";
      color: #0f172a;
      background: #fafafa;
      min-height: 100vh;
    }

    /* Page sizing: match Admin behavior (avoid right-side cut with left rail) */
    .explore-pin.page-wrap{
      --wv-content-pad: 16px;
      --wv-rail-w: 130px;
      --wv-right-safe: 0px;
      --wv-topbar-top: 70px;

      padding-left: var(--wv-content-pad) !important;
      padding-right: var(--wv-content-pad) !important;
      padding-top: 110px;

      width: calc(100vw - var(--wv-rail-w));
      width: calc(100dvw - var(--wv-rail-w));
      max-width: calc(100vw - var(--wv-rail-w));
      max-width: calc(100dvw - var(--wv-rail-w));

      overflow-x: visible;
    }

    @media (max-width: 820px){
      .explore-pin.page-wrap{
        --wv-rail-w: 0px;
        --wv-content-pad: 12px;
        width: 100vw;
        width: 100dvw;
        max-width: 100vw;
        max-width: 100dvw;
      }
    }

    /* Topbar (chips + search) aligned with Admin */
    .explore-pin .wv-topbar{
      position: fixed;
      top: var(--wv-topbar-top);
      left:112px;
      right: calc(var(--wv-content-pad) + var(--wv-right-safe));
      z-index: 1000;
      background: rgba(250,250,250,.92);
      backdrop-filter: blur(10px);
      padding: 10px 14px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
    }

    @media (max-width: 820px){
      .explore-pin .wv-topbar{ left: 12px; right: 12px; }
    }

    .explore-pin .wv-chip{
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 9px 12px;
      border-radius: 999px;
      cursor: pointer;
      user-select: none;
      font-weight: 600;
      font-size: 13px;
      line-height: 1;
      border: 1px solid rgba(15,23,42,.08);
      background: #fff;
      color: #0f172a;
      transition: transform .12s ease, box-shadow .15s ease;
      white-space: nowrap;
    }
    .explore-pin .wv-chip:hover{ transform: translateY(-1px); box-shadow: 0 10px 20px rgba(15,23,42,.08); }
    .explore-pin .wv-chip-active{ background: #111827; color: #fff; border-color: rgba(17,24,39,.45); }

    .explore-pin .wv-search{
      margin-left: 6px;
      display: inline-flex;
      align-items: center;
      gap: .55rem;
      padding: 8px 12px;
      border-radius: 999px;
      background: #f3f4f6;
      border: 1px solid rgba(15,23,42,.06);
      min-width: 260px;
      max-width: 760px;
      flex: 1;
    }
    .explore-pin .wv-search i{ color: #6b7280; }
    .explore-pin .wv-search input{
      border:none;
      outline:none;
      background:transparent;
      width:100%;
      font-size: 13px;
    }

    .explore-pin .wv-bell{
      margin-left: auto;
      position: relative;
    }
    .explore-pin .wv-bellBtn{
      display:inline-flex;
      align-items:center;
      justify-content:center;
      width: 38px;
      height: 38px;
      border-radius: 999px;
      border: 1px solid rgba(15,23,42,.08);
      background:#fff;
      cursor:pointer;
      transition: transform .12s ease, box-shadow .15s ease;
    }
    .explore-pin .wv-bellBtn:hover{ transform: translateY(-1px); box-shadow: 0 10px 20px rgba(15,23,42,.08); }
    .explore-pin .wv-bellCount{
      position:absolute;
      top: -6px;
      right: -6px;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      border-radius: 999px;
      background: #111827;
      color: #fff;
      font-size: 11px;
      font-weight: 800;
      display:flex;
      align-items:center;
      justify-content:center;
      border: 2px solid #fafafa;
    }

    .explore-pin .wv-pop{
      position:absolute;
      right: 0;
      top: calc(100% + 10px);
      width: min(420px, calc(100vw - 24px));
      background: #fff;
      border: 1px solid rgba(15,23,42,.10);
      border-radius: 16px;
      box-shadow: 0 24px 60px rgba(15,23,42,.18);
      overflow: hidden;
      z-index: 1100;
    }
    .explore-pin .wv-popHead{
      display:flex;
      align-items:center;
      justify-content:space-between;
      padding: 10px 12px;
      border-bottom: 1px solid rgba(15,23,42,.06);
      font-weight: 800;
      font-size: 13px;
    }
    .explore-pin .wv-popClose{
      border:0;
      background: rgba(15,23,42,.06);
      width: 30px;
      height: 30px;
      border-radius: 12px;
      cursor:pointer;
    }
    .explore-pin .wv-popClose:hover{ background: rgba(15,23,42,.10); }

    .explore-pin .wv-popList{
      max-height: 380px;
      overflow:auto;
    }
    .explore-pin .wv-popItem{
      padding: 10px 12px;
      display:flex;
      gap: 10px;
      border-bottom: 1px solid rgba(15,23,42,.06);
    }
    .explore-pin .wv-popItem:last-child{ border-bottom:none; }
    .explore-pin .wv-popBadge{
      width: 34px;
      height: 34px;
      border-radius: 999px;
      background: rgba(34,197,94,.12);
      color: rgba(21,128,61,.95);
      display:flex;
      align-items:center;
      justify-content:center;
      flex: 0 0 auto;
      border: 1px solid rgba(34,197,94,.25);
    }
    .explore-pin .wv-popMain{ min-width:0; }
    .explore-pin .wv-popTitle{
      font-weight: 800;
      font-size: 13px;
      margin: 0;
      color: rgba(15,23,42,.88);
      line-height: 1.15;
      overflow:hidden;
      text-overflow:ellipsis;
      white-space:nowrap;
    }
    .explore-pin .wv-popSub{
      font-size: 12px;
      color: rgba(15,23,42,.60);
      margin-top: 2px;
      display:flex;
      gap: 10px;
      align-items:center;
      flex-wrap:wrap;
    }
    .explore-pin .wv-popLink{
      font-weight: 800;
      color: rgba(37,99,235,.98);
      text-decoration:none;
    }
    .explore-pin .wv-popLink:hover{ text-decoration: underline; }
    .explore-pin .wv-popEmpty{
      padding: 18px 12px;
      color: rgba(15,23,42,.55);
      font-size: 13px;
    }

    .explore-pin .filters-spacer{ height: 76px; }

    /* Masonry: match Admin density while keeping random heights */
    .explore-pin .wv-masonry{
      width: 100%;
      max-width: 1680px;
      margin: 0 auto;

      column-count: 5;
      column-gap: 14px;
    }
    @media (max-width: 1580px){ .explore-pin .wv-masonry{ column-count: 4; } }
    @media (max-width: 1250px){ .explore-pin .wv-masonry{ column-count: 3; } }
    @media (max-width: 980px){  .explore-pin .wv-masonry{ column-count: 2; } }
    @media (max-width: 620px){  .explore-pin .wv-masonry{ column-count: 1; } }

    .explore-pin .wv-masonry-item{
      break-inside: avoid;
      page-break-inside: avoid;
      -webkit-column-break-inside: avoid;
      display: inline-block;
      width: 100%;
      margin: 0 0 14px;
    }

    /* Card */
    .explore-pin .wv-pin{
      background: #fff;
      border: 1px solid rgba(15,23,42,.08);
      border-radius: 18px;
      overflow: hidden;
      transition: transform .16s ease, box-shadow .18s ease;
      will-change: transform;
    }
    .explore-pin .wv-pin:hover{ transform: translateY(-2px); box-shadow: 0 18px 42px rgba(15,23,42,.12); }

    /* Cover */
    .explore-pin .wv-thumb-wrap{
      position: relative;
      width: 100%;
      background: #0b0f19;
      display: flex;
      align-items: center;
      justify-content: center;
      max-height: 420px; /* prevents mega-tall covers */
      overflow: hidden;
    }
    .explore-pin .wv-thumb{
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      object-position: center;
      background: transparent;
    }

    .explore-pin .wv-skeleton{
      width: 100%;
      height: 100%;
      animation: wvShimmer 1.2s infinite linear;
      background: linear-gradient(90deg, #111827, #1f2937, #111827);
      background-size: 200% 100%;
      opacity: .22;
      min-height: 160px;
    }
    @keyframes wvShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    /* Skeleton lines */
    .explore-pin .wv-skel-line{
      height: 10px;
      border-radius: 999px;
      background: rgba(15,23,42,.10);
      margin-top: 10px;
    }
    .explore-pin .wv-skel-line--title{ height: 12px; margin-top: 0; width: 78%; }
    .explore-pin .wv-skel-row{ display:flex; align-items:center; gap: 10px; margin-top: 12px; }
    .explore-pin .wv-skel-avatar{
      width: 28px; height: 28px; border-radius: 999px; background: rgba(15,23,42,.10);
      flex: 0 0 auto;
    }

    /* Body */
    .explore-pin .wv-body{ padding: 12px 12px 12px; }

    .explore-pin .wv-titleRow{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap: 10px;
    }

    .explore-pin .wv-title{
      margin: 0;
      font-weight: 500;
      font-size: 15px;
      line-height: 1.2;
      min-width: 0;
    }

    .explore-pin .wv-desc{
      margin: 6px 0 0;
      color: rgba(15,23,42,.62);
      font-size: 13px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      min-height: 2.4em;
    }

    .explore-pin .wv-meta{
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 10px;
      margin-top: 10px;
    }

    /* Owner identity */
    .explore-pin .wv-owner{ min-width:0; }

    .explore-pin .wv-ownerLink{
      display:inline-flex;
      align-items:center;
      gap: 8px;
      text-decoration:none;
      color: inherit;
      min-width:0;
    }
    .explore-pin .wv-ownerLink--disabled{ cursor: default; }

    .explore-pin .wv-ownerInner{ display:inline-flex; align-items:center; gap: 8px; min-width:0; }

    .explore-pin .wv-avatar{
      width: 28px;
      height: 28px;
      border-radius: 999px;
      background: #eef2ff;
      border: 1px solid rgba(15,23,42,.10);
      display:flex;
      align-items:center;
      justify-content:center;
      overflow:hidden;
      flex: 0 0 auto;
    }
    .explore-pin .wv-avatarImg{ width:100%; height:100%; object-fit: cover; display:block; }
    .explore-pin .wv-avatarFallback{ font-weight: 900; font-size: 11px; color: rgba(15,23,42,.70); }

    .explore-pin .wv-ownerText{
      display:flex;
      gap: 8px;
      align-items: baseline;
      min-width:0;
    }
    .explore-pin .wv-ownerUser{
      font-weight: 500;
      font-size: 13px;
      color: rgba(15,23,42,.86);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
    }
    .explore-pin .wv-ownerSurname{
      font-weight: 700;
      font-size: 12px;
      color: rgba(15,23,42,.58);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
    }

    .explore-pin .wv-pills{
      display:flex;
      align-items:center;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .explore-pin .wv-pill{
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 7px 10px;
      border-radius: 999px;
      border: 1px solid rgba(15,23,42,.10);
      background: rgba(255,255,255,.9);
      font-size: 12px;
      font-weight: 700;
      color: rgba(15,23,42,.70);
      line-height: 1;
      white-space: nowrap;
    }

    .explore-pin .wv-pill-ok{
      border-color: rgba(34,197,94,.35);
      background: rgba(34,197,94,.10);
      color: rgba(21,128,61,.95);
    }

    /* Wizard-like buttons */
    .explore-pin .btn-weave{
      border-radius: 10px !important;
      padding: .50rem .9rem !important;
      font-weight: 500;
      font-size: 13px;
      display: inline-flex;
      align-items: center;
      gap: .5rem;
      box-shadow: 0 6px 18px rgba(0,0,0,.06);
    }
    .explore-pin .btn-weave:disabled{ box-shadow: none; }

    .explore-pin .wv-actions{
      margin-top: 10px;
      display:flex;
      justify-content:flex-end;
      align-items:center;
      gap: 10px;
      flex-wrap: wrap;
    }

    .explore-pin .wv-statusText{
      font-size: 12px;
      font-weight: 700;
      color: rgba(15,23,42,.62);
      display:flex;
      align-items:center;
      gap: 8px;
    }

    .explore-pin .wv-empty{
      text-align:center;
      color: rgba(15,23,42,.55);
      padding: 56px 0;
    }

    .explore-pin .wv-bottomSpacer{
      height: 32px;
    }
  `;

  // Build view model for cards (memoized, minimal computations per render)
  const cardsVm = useMemo(() => {
    const bust = avatarBustRef.current; // used to refresh ui-avatars when users loaded (kept stable otherwise)
    void bust;

    return visibleProjects.map((p) => {
      const ownerUsername = String(p?.created_by || "").trim() || t("anonymous");
      const u = usersByUsername?.[ownerUsername];
      const ownerSurname = resolveSurname(u);
      const ownerLabelForFallback = (ownerUsername.includes("@") ? ownerUsername.split("@")[0] : ownerUsername) || ownerSurname || ownerUsername;
      const ownerAvatarSrc = avatarFor(ownerUsername, ownerLabelForFallback);
      const ownerProfilePath = resolveUserProfilePath(ownerUsername);

      const participantsCount = Array.isArray(rolesMap?.[p.id]) ? rolesMap[p.id].length : undefined;

      return {
        project: p,
        coverSrc: coverSrcById?.[p.id] || (p?.identity_image ? `/uploads/${p.identity_image}` : makeSeedImage(p.title, p.id)),
        isPublic: isPublicProject(p),
        ownerUsername,
        ownerSurname,
        ownerAvatarSrc,
        ownerProfilePath,
        participantsCount,
        createdAt: formatDate(p?.created_at || p?.createdAt || p?.date),
        requestSent: !!requestSentProjects?.[p.id],
      };
    });
  }, [
    visibleProjects,
    usersByUsername,
    rolesMap,
    coverSrcById,
    requestSentProjects,
    avatarFor,
    resolveSurname,
    resolveUserProfilePath,
    isPublicProject,
    formatDate,
    t,
  ]);

  // Notifications badge count (simple: number of items)
  const notifCount = notifItems.length;

  return (
    <div className="page-wrap explore-pin">
      <style>{styles}</style>

      {notice && (
        <div className={`wv-notice wv-notice--${notice.type || "info"}`} role="status">
          <span>{notice.text}</span>
          <button className="wv-noticeClose" onClick={() => setNotice(null)} aria-label="Close">
            ×
          </button>
        </div>
      )}

      {/* Top bar */}
      <div ref={topbarRef} className="wv-topbar" onClick={() => setNotifOpen(false)}>
        <div
          className={`wv-chip ${visibilityFilter === "all" ? "wv-chip-active" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            setVisibilityFilter("all");
          }}
          role="button"
          tabIndex={0}
        >
          <i className="bi bi-grid" /> {t("chip_all")}
        </div>

        <div
          className={`wv-chip ${visibilityFilter === "public" ? "wv-chip-active" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            setVisibilityFilter("public");
          }}
          role="button"
          tabIndex={0}
        >
          <i className="bi bi-globe" /> {t("chip_public")}
        </div>

        <div
          className={`wv-chip ${visibilityFilter === "private" ? "wv-chip-active" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            setVisibilityFilter("private");
          }}
          role="button"
          tabIndex={0}
        >
          <i className="bi bi-lock" /> {t("chip_private")}
        </div>

        

        {/* Notifications */}
       
      </div>

      {/* Dynamic spacer = topbar height */}

      {/* Content */}
      {isLoadingInitial && eligibleProjects.length === 0 ? (
        <div className="wv-masonry" aria-label={t("loading_projects")}>
          {Array.from({ length: 14 }).map((_, i) => (
            <SkeletonPin key={`sk-${i}`} i={i} />
          ))}
        </div>
      ) : visibleProjects.length === 0 ? (
        <div className="wv-empty">{t("empty")}</div>
      ) : (
        <>
          <div className="wv-masonry">
            {cardsVm.map((vm) => (
              <ProjectCard
                key={vm.project.id}
                project={vm.project}
                coverSrc={vm.coverSrc}
                ownerUsername={vm.ownerUsername}
                ownerSurname={vm.ownerSurname}
                ownerAvatarSrc={vm.ownerAvatarSrc}
                ownerProfilePath={vm.ownerProfilePath}
                isPublic={vm.isPublic}
                participantsCount={vm.participantsCount}
                createdAt={vm.createdAt}
                requestSent={vm.requestSent}
                t={t}
                onCardSeen={onCardSeen}
                onOpenRead={openRead}
                onOpenWeave={openWeave}
                onRequestAccess={onRequestAccess}
                onPrimaryClick={handlePrimaryClick}
              />
            ))}

            {/* Skeletons while loading more (non-blocking) */}
            {isLoadingMore
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonPin key={`skm-${i}`} i={i + 20} />)
              : null}
          </div>

          {/* Sentinel for infinite scroll */}
          <div ref={sentinelRef} className="wv-bottomSpacer" aria-hidden="true" />
        </>
      )}
    </div>
  );
}
